import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from 'redux/modules/auth';
import type { RootState } from '../redux/modules';
import isAdmin from '../utils/isAdmin';

// Parametrizável para teste em dev
const DEFAULT_SESSION_DURATION = 60 * 60 * 1000; // 60 minutes in ms
// const DEFAULT_SESSION_DURATION = 2 * 60 * 1000; // 15 minutes in ms
// const DEFAULT_WARNING_DURATION = 60 * 1000; // 5 minutes in ms
const DEFAULT_WARNING_DURATION = 5 * 60 * 1000; // 5 minutes in ms

export type ContextSessionType = {
  timeLeft: number;
  isWarningOpen: boolean;
  renewSession: () => void;
  closeWarningModal: () => void;
};

const SessionContext = createContext({} as ContextSessionType);

export const SessionProvider = ({
  children,
  sessionDuration = DEFAULT_SESSION_DURATION,
  warningDuration = DEFAULT_WARNING_DURATION,
}) => {
  const [timeLeft, setTimeLeft] = useState(sessionDuration);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const sessionTimeoutRef = useRef<any>(null);
  const warningTimeoutRef = useRef<any>(null);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const isThisUserAnAdmin = isAdmin(user);

  // Clear timers to prevent memory leak
  const clearTimeouts = () => {
    if (sessionTimeoutRef.current) {
      // if (sessionTimeoutRef.current.interval) {
      //   clearInterval(sessionTimeoutRef.current.interval);
      // }
      clearTimeout(sessionTimeoutRef.current);
    }
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
  };

  // Function to automatic logout
  const handleLogout = async () => {
    clearTimeouts();
    setTimeLeft(0);
    setIsWarningOpen(false);
    dispatch(logout());
  };

  // Start/restart session
  const startSession = () => {
    clearTimeouts();
    setTimeLeft(sessionDuration);
    setIsWarningOpen(false);

    // Timer para warning
    warningTimeoutRef.current = setTimeout(() => {
      setIsWarningOpen(true);
    }, sessionDuration - warningDuration);

    // Timer para logout
    sessionTimeoutRef.current = setTimeout(() => {
      handleLogout();
    }, sessionDuration);

    // Optional: Timer to show countdown
    // const interval = setInterval(() => {
    //   setTimeLeft((prev) => {
    //     if (prev <= 1000) {
    //       clearInterval(interval);
    //       return 0;
    //     }
    //     return prev - 1000;
    //   });
    // }, 1000);
    // // Make clear
    // sessionTimeoutRef.current = {
    //   ...sessionTimeoutRef.current,
    //   interval,
    // };
  };

  // Function to renew session
  const renewSession = () => {
    startSession();
  };

  const closeWarningModal = () => {
    setIsWarningOpen(false);
  };

  // Start da sessão (ex: ao login)
  useEffect(() => {
    if (!isThisUserAnAdmin) {
      startSession();
    }
    // Cleanup geral
    return () => clearTimeouts();
    // eslint-disable-next-line
  }, []);

  return (
    <SessionContext.Provider
      value={{
        timeLeft,
        isWarningOpen,
        renewSession,
        closeWarningModal,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
